import { Alert, Button, ColorInput, FileButton, Group, Image, Paper, Stack, Text, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding, useDeleteLogo, useSaveBranding, useUploadLogo, type Branding } from '@/api/sso';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';

/** Branding (WS-06): Logo, Farbe, Kürzel/Subdomain und eigene Domain. */
export function BrandingSettings() {
  const ctx = useCurrentWorkspace();
  const { data } = useBranding(ctx.workspaceId);
  if (!data) return null;
  return <BrandingForm key={JSON.stringify(data)} data={data} />;
}

function BrandingForm({ data }: { data: Branding }) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const save = useSaveBranding(ctx.workspaceId);
  const upload = useUploadLogo(ctx.workspaceId);
  const removeLogo = useDeleteLogo(ctx.workspaceId);
  const onError = useApiError();
  const [slug, setSlug] = useState(data.slug ?? '');
  const [color, setColor] = useState(data.brandColor ?? '');
  const [domain, setDomain] = useState(data.customDomain ?? '');
  const loginLink = slug ? `${window.location.origin}/login?ws=${slug}` : null;

  return (
    <Stack maw={720}>
      {!data.available && <Alert color="gray">{t('settings.notInPlan')}</Alert>}
      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          {t('branding.logo')}
        </Title>
        <Group>
          {data.logoUrl ? (
            <Image src={data.logoUrl} h={48} w="auto" fit="contain" alt={t('branding.logo')} />
          ) : (
            <Text size="sm" c="dimmed">
              {t('branding.noLogo')}
            </Text>
          )}
          <FileButton accept="image/png,image/jpeg,image/webp" onChange={(f) => f && upload.mutate(f, { onError })}>
            {(props) => (
              <Button {...props} variant="default" leftSection={<IconUpload size={16} />} loading={upload.isPending} disabled={!data.available}>
                {t('branding.uploadLogo')}
              </Button>
            )}
          </FileButton>
          {data.logoUrl && (
            <Button variant="subtle" color="red" onClick={() => removeLogo.mutate(undefined, { onError })}>
              {t('common.remove')}
            </Button>
          )}
        </Group>
        <Text size="xs" c="dimmed" mt="xs">
          {t('branding.logoHint')}
        </Text>
      </Paper>
      <Paper withBorder p="md">
        <Stack>
          <ColorInput label={t('branding.color')} description={t('branding.colorHint')} value={color} onChange={setColor} format="hex" disallowInput={false} w={240} />
          <TextInput
            label={t('branding.slug')}
            description={data.subdomainUrl ? t('branding.slugHintSubdomain', { url: data.subdomainUrl }) : t('branding.slugHint')}
            value={slug}
            onChange={(e) => setSlug(e.currentTarget.value.toLowerCase())}
            placeholder="acme"
          />
          {loginLink && (
            <Text size="sm">
              {t('branding.loginLink')}: <code>{loginLink}</code>
            </Text>
          )}
          <TextInput
            label={t('branding.domain')}
            description={t('branding.domainHint')}
            value={domain}
            onChange={(e) => setDomain(e.currentTarget.value)}
            placeholder="time.example.com"
          />
          <Group>
            <Button
              disabled={!data.available}
              loading={save.isPending}
              onClick={() =>
                save.mutate(
                  { slug, brandColor: color, customDomain: domain },
                  { onSuccess: () => notifications.show({ color: 'green', message: t('common.saved') }), onError },
                )
              }
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
